import { useEffect, useMemo, useState } from "react";
import { Box, Modal, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import DropDown from "@/components/forms/inputs/dropDown";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { api } from "@/helper/api";
import { RootState } from "@/components/state/store";
import SyncIcon from "@mui/icons-material/Sync";
import styles from "@/components/appointment/appointmentBatchCreateModal.module.css";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import GenericDateTime from "@/components/dateTime/genericDateTime";
import GenericTime from "@/components/dateTime/genericTime";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import {
  DEFAULT_KAER_TEAM_ID,
  KAER_COMPANY_SUBJECT_ID,
} from "@/helper/systemIds";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface BatchRow {
  id: string;
  subject: any | null;
  examinations: any[];
  time: string;
}

type RowError = {
  subject?: string;
  examinations?: string;
};

const createEmptyRow = (): BatchRow => ({
  id: `${Date.now()}-${Math.random()}`,
  subject: null,
  examinations: [],
  time: "",
});

const isRowFilled = (row: BatchRow) =>
  Boolean(row.subject?.value) || row.examinations.length > 0;

const withTrailingEmptyRow = (rows: BatchRow[]) => {
  if (rows.length === 0) {
    return [createEmptyRow()];
  }
  const lastRow = rows[rows.length - 1];
  if (isRowFilled(lastRow)) {
    return [...rows, createEmptyRow()];
  }
  return rows;
};

const getSafeSlotDuration = (slotDurationMinutes: number) =>
  Number.isFinite(slotDurationMinutes) ? Math.max(1, slotDurationMinutes) : 1;

const addMinutesToTimeString = (time: string, minutesToAdd: number): string => {
  const [hours, minutes] = time.split(":").map(Number);
  const base = dayjs()
    .hour(hours || 0)
    .minute(minutes || 0)
    .second(0);
  return base.add(minutesToAdd, "minute").format("HH:mm");
};

const buildTimeForRow = (
  baseDateTime: Dayjs | null,
  slotDurationMinutes: number,
  rowIndex: number,
) => {
  const validBaseDate = baseDateTime && baseDateTime.isValid();
  const initial = validBaseDate ? (baseDateTime as Dayjs) : dayjs();
  return initial
    .add(rowIndex * getSafeSlotDuration(slotDurationMinutes), "minute")
    .format("HH:mm");
};

const getEffectiveRowTime = (
  rows: BatchRow[],
  index: number,
  baseDateTime: Dayjs | null,
  slotDurationMinutes: number,
): string => {
  let lastExplicitIndex = -1;
  for (let i = index; i >= 0; i -= 1) {
    if (rows[i]?.time) {
      lastExplicitIndex = i;
      break;
    }
  }

  if (lastExplicitIndex === -1) {
    return buildTimeForRow(baseDateTime, slotDurationMinutes, index);
  }

  const steps = index - lastExplicitIndex;
  if (steps === 0) {
    return rows[lastExplicitIndex].time;
  }

  return addMinutesToTimeString(
    rows[lastExplicitIndex].time,
    steps * getSafeSlotDuration(slotDurationMinutes),
  );
};

const getDateTimeFromDateAndTime = (baseDate: Dayjs, time: string): string => {
  const [hours, minutes] = time.split(":").map(Number);
  return baseDate
    .hour(hours || 0)
    .minute(minutes || 0)
    .second(0)
    .millisecond(0)
    .toISOString();
};

const getDayjsFromTime = (time: string): Dayjs | null => {
  const [hour, minute] = time.split(":").map(Number);
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  return dayjs()
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0);
};

const isPersistedCompanySelection = (value: any): boolean => {
  if (!value || typeof value !== "object" || Object.keys(value).length === 0) {
    return false;
  }
  if ("company_id" in value) {
    return false;
  }
  if ("companyId" in value) {
    return true;
  }
  if (
    value.is_company === true ||
    value.isParent === true ||
    value.hasParent === true
  ) {
    return true;
  }
  return Boolean(value.value && value.label);
};

export default function AppointmentBatchCreateModal({
  open,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const persistedSubjectSearchValue = useSelector(
    (state: RootState) => state.subjectSearch,
  );

  const { handleError, errorModal } = useErrorModal(undefined, {
    confirmButtonText: t("ok_capital"),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [company, setCompany] = useState<any>(null);
  const [mainCategory, setMainCategory] = useState<any>(null);
  const [executedBy, setExecutedBy] = useState<any>(null);
  const [assistedBy, setAssistedBy] = useState<any>(null);
  const [building, setBuilding] = useState<any>(null);
  const [onSite, setOnSite] = useState<boolean | null>(null);
  const [dateTime, setDateTime] = useState<Dayjs | null>(dayjs());
  const [slotDuration, setSlotDuration] = useState<number>(30);
  const [buildingResetSignal, setBuildingResetSignal] = useState(0);

  const [mainCategoryOptions, setMainCategoryOptions] = useState<any[]>([]);
  const [examinationOptions, setExaminationOptions] = useState<any[]>([]);
  const [buildingOptions, setBuildingOptions] = useState<any[]>([]);
  const [rows, setRows] = useState<BatchRow[]>([createEmptyRow()]);

  const [formErrors, setFormErrors] = useState<{
    company?: string;
    mainCategory?: string;
    dateTime?: string;
    slotDuration?: string;
    rows?: string;
    rowErrors: Record<string, RowError>;
  }>({ rowErrors: {} });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, string>({
      fieldOrder: [],
    });

  const filledRows = useMemo(
    () => rows.filter((row) => isRowFilled(row)),
    [rows],
  );
  const canSynchronizeExaminations = useMemo(
    () => rows.length > 0 && (rows[0]?.examinations?.length || 0) > 0,
    [rows],
  );
  const onSiteOptions = [
    { label: t("yes"), value: true },
    { label: t("no"), value: false },
  ];

  const resetBuildingAndOnSite = () => {
    setBuilding(null);
    setOnSite(null);
    setBuildingResetSignal((signal) => signal + 1);
  };

  const handleCompanyChange = (value: any) => {
    setCompany(value);
    resetBuildingAndOnSite();
    setFormErrors((prev) => ({ ...prev, company: undefined }));
  };

  const handleBuildingChange = (value: any) => {
    setBuilding(value);
    if (value?.value) {
      setOnSite(true);
    } else {
      setOnSite(null);
    }
  };

  const handleOnSiteChange = (selectedOption: any) => {
    if (typeof selectedOption?.value === "boolean") {
      setOnSite(selectedOption.value);
    }
  };

  useEffect(() => {
    if (!open) return;
    api.genericApiRequest({
      entity: "mainCategory",
      method: "get",
      parametersToRender: { depth: "0" },
      successHandler: (res: any) => {
        const options = (res.data.results || []).map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setMainCategoryOptions(options);
      },
      failHandler: (apiError: any) => {
        handleError(apiError);
      },
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!company?.value) {
      setBuildingOptions([]);
      resetBuildingAndOnSite();
      return;
    }
    api.genericApiRequest({
      entity: "building",
      method: "get",
      parametersToRender: {
        depth: "0",
        additionalUrlParameters: { subject_id: company.value },
      },
      successHandler: (res: any) => {
        const options = (res.data.results || []).map((item: any) => ({
          value: item.id,
          label: `${item.name} (${item.street_house || ""}, ${item.zip || ""} ${
            item.city || ""
          })`,
        }));
        setBuildingOptions(options);
      },
      failHandler: (apiError: any) => {
        handleError(apiError);
      },
    });
  }, [company?.value, open]);

  useEffect(() => {
    if (open) return;
    setCompany(null);
    setMainCategory(null);
    setExecutedBy(null);
    setAssistedBy(null);
    setBuilding(null);
    setOnSite(null);
    setBuildingResetSignal(0);
    setDateTime(dayjs());
    setSlotDuration(30);
    setRows([createEmptyRow()]);
    setFormErrors({ rowErrors: {} });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (company?.value) return;

    if (isPersistedCompanySelection(persistedSubjectSearchValue)) {
      setCompany(persistedSubjectSearchValue);
      setFormErrors((prev) => ({ ...prev, company: undefined }));
    }
  }, [open, persistedSubjectSearchValue, company?.value]);

  const updateRow = (rowId: string, patch: Partial<BatchRow>) => {
    setRows((prev) => {
      const updated = prev.map((row) =>
        row.id === rowId ? { ...row, ...patch } : row,
      );
      return withTrailingEmptyRow(updated);
    });
    setFormErrors((prev) => ({
      ...prev,
      rows: undefined,
      rowErrors: {
        ...prev.rowErrors,
        [rowId]: { subject: undefined, examinations: undefined },
      },
    }));
  };

  const loadEmployeeOptions = (inputValue: string): Promise<any[]> => {
    if (inputValue.trim().length < 3) return Promise.resolve([]);
    return new Promise((resolve) => {
      api.genericApiRequest({
        entity: "user",
        method: "get",
        parametersToRender: {
          search: inputValue.trim(),
          depth: "0",
          additionalUrlParameters: {
            impersonate_subject: KAER_COMPANY_SUBJECT_ID,
            is_active: "true",
          },
        },
        successHandler: (res: any) => {
          const options = (res.data.results || [])
            .map((item: any) => ({
              value: item.subject_id,
              label: `${item.last_name || ""}, ${item.first_name || ""}`.trim(),
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
          resolve(options);
        },
        failHandler: () => {
          resolve([]);
        },
      });
    });
  };

  useEffect(() => {
    if (!open) return;
    if (!mainCategory?.value) {
      setExaminationOptions([]);
      return;
    }
    api.genericApiRequest({
      entity: "examination",
      method: "get",
      parametersToRender: {
        depth: "0",
        limit: 500,
        additionalUrlParameters: {
          include_relations: true,
          category_set: mainCategory.value,
        },
      },
      successHandler: (res: any) => {
        const options = (res.data.results || []).map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setExaminationOptions(options);
      },
      failHandler: (apiError: any) => {
        handleError(apiError);
        setExaminationOptions([]);
      },
    });
  }, [mainCategory?.value, open]);

  const syncExaminationsFromFirstRow = () => {
    if (rows.length < 2) return;
    const source = rows[0]?.examinations || [];
    setRows((prev) =>
      withTrailingEmptyRow(
        prev.map((row, index) =>
          index === 0 ? row : { ...row, examinations: source },
        ),
      ),
    );
  };

  const validate = () => {
    const nextErrors: {
      company?: string;
      mainCategory?: string;
      dateTime?: string;
      slotDuration?: string;
      rows?: string;
      rowErrors: Record<string, RowError>;
    } = { rowErrors: {} };
    const invalidTargets: string[] = [];

    if (!company?.value) {
      nextErrors.company = t("field_required");
      invalidTargets.push("company");
    }
    if (!mainCategory?.value) {
      nextErrors.mainCategory = t("field_required");
      invalidTargets.push("mainCategory");
    }
    if (!dateTime || !dateTime.isValid()) {
      nextErrors.dateTime = t("field_required");
      invalidTargets.push("dateTime");
    }
    if (!slotDuration || slotDuration < 1) {
      nextErrors.slotDuration = t("field_required");
      invalidTargets.push("slotDuration");
    }

    if (filledRows.length === 0) {
      nextErrors.rows = t("field_required");
      if (rows[0]) {
        invalidTargets.push(`row-${rows[0].id}-subject`);
      }
    }

    rows.forEach((row) => {
      if (!isRowFilled(row)) return;
      const rowError: RowError = {};
      if (!row.subject?.value) {
        rowError.subject = t("field_required");
        invalidTargets.push(`row-${row.id}-subject`);
      }
      if (!row.examinations?.length) {
        rowError.examinations = t("field_required");
        invalidTargets.push(`row-${row.id}-examinations`);
      }
      if (Object.keys(rowError).length > 0) {
        nextErrors.rowErrors[row.id] = rowError;
      }
    });

    setFormErrors(nextErrors);
    focusFirstInvalidField({}, invalidTargets);
    return (
      !nextErrors.company &&
      !nextErrors.mainCategory &&
      !nextErrors.dateTime &&
      !nextErrors.slotDuration &&
      !nextErrors.rows &&
      Object.keys(nextErrors.rowErrors).length === 0
    );
  };

  const createAppointmentForRow = async (row: BatchRow, rowIndex: number) => {
    const rowTime = getEffectiveRowTime(rows, rowIndex, dateTime, slotDuration);
    const safeDateTime = dateTime && dateTime.isValid() ? dateTime : dayjs();
    const appointmentPayload: any = {
      datetime: getDateTimeFromDateAndTime(safeDateTime, rowTime),
      subject: row.subject.value,
      team: DEFAULT_KAER_TEAM_ID,
      executed_by: executedBy?.value || undefined,
      assisted_by: assistedBy?.value || undefined,
      category: mainCategory?.value,
      on_site: onSite,
    };

    Object.keys(appointmentPayload).forEach((key) => {
      if (
        appointmentPayload[key] === undefined ||
        appointmentPayload[key] === null
      ) {
        delete appointmentPayload[key];
      }
    });

    const appointmentResponse = await new Promise<any>((resolve, reject) => {
      api.genericApiRequest({
        entity: "appointment",
        method: "post",
        submitData: appointmentPayload,
        successHandler: (res: any) => resolve(res),
        failHandler: (apiError: any) => reject(apiError),
      });
    });

    const appointmentId = appointmentResponse?.data?.id;
    const buildingInformationId =
      appointmentResponse?.data?.building_information?.id;

    if (appointmentId && building?.value && buildingInformationId) {
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "buildingInformation",
          method: "put",
          entityId: buildingInformationId,
          submitData: { building: building.value },
          successHandler: () => resolve(),
          failHandler: (apiError: any) => reject(apiError),
        });
      });
    }

    if (appointmentId && row.examinations.length > 0) {
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "put",
          entityId: appointmentId,
          submitData: {
            examination_set: row.examinations.map((exam: any) => exam.value),
          },
          successHandler: () => resolve(),
          failHandler: (apiError: any) => reject(apiError),
        });
      });
    }

    if (appointmentId) {
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "put",
          entityId: appointmentId,
          submitData: { status: "open" },
          successHandler: () => resolve(),
          failHandler: (apiError: any) => reject(apiError),
        });
      });
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!validate()) return;

    try {
      setIsSaving(true);

      const rowsToCreate = rows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => isRowFilled(row));

      for (const { row, index } of rowsToCreate) {
        await createAppointmentForRow(row, index);
      }

      const count = rowsToCreate.length;
      const label =
        count === 1 ? t("appointment_single") : t("appointment_multi");
      setSuccessMessage(t("create_successful") + ` (${count} ${label})`);
      setShowSuccessNotification(true);

      onSuccess?.();
      onClose();
    } catch (apiError: any) {
      handleError(apiError);
    } finally {
      setIsSaving(false);
    }
  };

  useModalDismiss(open, onClose);
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: handleSave,
  });

  return (
    <>
      <Modal
        open={open}
        disableAutoFocus
        onClose={(_event, reason) => {
          if (reason === "backdropClick") return;
          onClose();
        }}
      >
        <Box className={styles.modalBackdrop}>
          <form
            className={styles.modalWrapper}
            onSubmit={handleSubmit}
            noValidate>
            <h2 className={styles.heading}>{t("add_multiple_appointments")}</h2>

            <div className={styles.fixedSectionGrid}>
              <div ref={registerValidationTarget("company")}>
                <FormFieldLabel
                  label={t("company")}
                  htmlFor="batch-company"
                  mandatory
                />
                <DropDownSubjectSearch
                  name="batch-company"
                  subdomain={subdomain}
                  queryParameter="companies"
                  onChange={handleCompanyChange}
                  preExistingValue={
                    company ||
                    (isPersistedCompanySelection(persistedSubjectSearchValue)
                      ? persistedSubjectSearchValue
                      : undefined)
                  }
                />
                {formErrors.company && (
                  <span className={styles.errorText}>{formErrors.company}</span>
                )}
              </div>

              <div ref={registerValidationTarget("mainCategory")}>
                <DropDown
                  name="main_category"
                  label={t("main_category")}
                  options={mainCategoryOptions}
                  onChange={(value: any) => {
                    setMainCategory(value);
                    setRows((prev) =>
                      withTrailingEmptyRow(
                        prev.map((row) => ({ ...row, examinations: [] })),
                      ),
                    );
                    setFormErrors((prev) => ({
                      ...prev,
                      mainCategory: undefined,
                    }));
                  }}
                  value={mainCategory ? [mainCategory] : []}
                  mandatory
                />
                {formErrors.mainCategory && (
                  <span className={styles.errorText}>
                    {formErrors.mainCategory}
                  </span>
                )}
              </div>

              <div>
                <DropDown
                  name="building"
                  label={t("buildings")}
                  options={buildingOptions}
                  onChange={handleBuildingChange}
                  value={building ? [building] : []}
                  resetSignal={buildingResetSignal}
                />
              </div>

              <div>
                <DropDown
                  name="on_site"
                  label={t("on_site")}
                  options={onSiteOptions}
                  externalValue={
                    onSiteOptions.find((option) => option.value === onSite) ??
                    null
                  }
                  onChange={handleOnSiteChange}
                />
              </div>

              <div>
                <FormFieldLabel
                  label={t("executed_by")}
                  htmlFor="executed_by"
                />
                <SubjectSearchSelect
                  name="executed_by"
                  loadOptions={loadEmployeeOptions}
                  onChange={(value: any) => setExecutedBy(value)}
                  value={executedBy}
                  isClearable
                  placeholder={t("search_placeholder")}
                />
              </div>

              <div>
                <FormFieldLabel
                  label={t("assisted_by")}
                  htmlFor="assisted_by"
                />
                <SubjectSearchSelect
                  name="assisted_by"
                  loadOptions={loadEmployeeOptions}
                  onChange={(value: any) => setAssistedBy(value)}
                  value={assistedBy}
                  isClearable
                  placeholder={t("search_placeholder")}
                />
              </div>

              <div ref={registerValidationTarget("dateTime")}>
                <label className={styles.label}>
                  {t("datetime")}
                  <span className={styles.required}>*</span>
                </label>
                <GenericDateTime
                  value={dateTime}
                  className={styles.dateTimePicker}
                  clearable={false}
                  textFieldProps={{
                    sx: {
                      "& .MuiInputBase-input.MuiOutlinedInput-input": {
                        padding: "0.8rem",
                      },
                    },
                  }}
                  onChange={(value) => {
                    setDateTime(value);
                    setFormErrors((prev) => ({ ...prev, dateTime: undefined }));
                  }}
                />
                {formErrors.dateTime && (
                  <span className={styles.errorText}>
                    {formErrors.dateTime}
                  </span>
                )}
              </div>

              <div ref={registerValidationTarget("slotDuration")}>
                <label className={styles.label}>
                  {t("duration")}
                  <span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.slotInput}
                  type="number"
                  min={1}
                  value={slotDuration}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value);
                    setSlotDuration(nextValue);
                    setFormErrors((prev) => ({
                      ...prev,
                      slotDuration: undefined,
                    }));
                  }}
                />
                {formErrors.slotDuration && (
                  <span className={styles.errorText}>
                    {formErrors.slotDuration}
                  </span>
                )}
              </div>
            </div>

            {company?.value && mainCategory?.value && (
              <>
                <div className={styles.rowsHeader}>
                  <h4>{t("individual_appointments")}</h4>
                  <Tooltip
                    arrow
                    placement="top"
                    title={t("synchronize_examinations_tooltip")}
                  >
                    <span>
                      <GenericButton
                        variant="outlined"
                        startIcon={<SyncIcon />}
                        onClick={syncExaminationsFromFirstRow}
                        disabled={!canSynchronizeExaminations}
                      >
                        {t("synchronize_examinations")}
                      </GenericButton>
                    </span>
                  </Tooltip>
                </div>

                <div className={styles.rowsWrapper}>
                  {rows.map((row, index) => {
                    const rowTime = getEffectiveRowTime(
                      rows,
                      index,
                      dateTime,
                      slotDuration,
                    );
                    const rowError = formErrors.rowErrors[row.id] || {};
                    return (
                      <div key={row.id} className={styles.rowGrid}>
                        <div
                          ref={registerValidationTarget(
                            `row-${row.id}-subject`,
                          )}
                        >
                          <FormFieldLabel
                            label={t("person")}
                            htmlFor={`batch-subject-${row.id}`}
                            mandatory
                          />
                          <DropDownSubjectSearch
                            name={`batch-subject-${row.id}`}
                            subdomain={subdomain}
                            queryParameter="users"
                            subjectIdForFiltering={company?.value}
                            preExistingValue={row.subject}
                            placeholder={t("search_placeholder")}
                            disabled={!company?.value}
                            isClearable
                            onChange={(value: any) =>
                              updateRow(row.id, { subject: value })
                            }
                          />
                          {rowError.subject && (
                            <span className={styles.errorText}>
                              {rowError.subject}
                            </span>
                          )}
                        </div>

                        <div>
                          <label className={styles.label}>
                            {t("clock_time")}
                            <span className={styles.required}>*</span>
                          </label>
                          <GenericTime
                            value={getDayjsFromTime(rowTime)}
                            clearable={false}
                            onChange={(value) => {
                              const formatted = value
                                ? value.format("HH:mm")
                                : "";
                              updateRow(row.id, { time: formatted });
                            }}
                            textFieldProps={{
                              required: true,
                              sx: {
                                "& .MuiInputBase-input.MuiOutlinedInput-input": {
                                  padding: "0.9rem",
                                  minWidth: "2.7rem",
                                },
                                width: "100%",
                              },
                            }}
                          />
                        </div>

                        <div
                          ref={registerValidationTarget(
                            `row-${row.id}-examinations`,
                          )}
                        >
                          <DropDown
                            name={`examinations-${row.id}`}
                            label={t("examinations")}
                            placeholder={t("search_placeholder")}
                            options={examinationOptions}
                            isMulti
                            mandatory
                            isClearable
                            isSearchable
                            disabled={!mainCategory?.value}
                            externalValue={row.examinations}
                            onChange={(value: any) =>
                              updateRow(row.id, {
                                examinations: Array.isArray(value) ? value : [],
                              })
                            }
                          />
                          {rowError.examinations && (
                            <span className={styles.errorText}>
                              {rowError.examinations}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {formErrors.rows && (
                  <span className={styles.errorText}>{formErrors.rows}</span>
                )}
              </>
            )}

            <div className={styles.buttonRow}>
              <GenericButton variant="outlined" onClick={onClose}>
                {t("cancel")}
              </GenericButton>
              <GenericButton
                type="submit"
                waitingForResponse={isSaving}
                showSpinnerOnDisable={true}
                loading={isSubmitting}
                disabled={isSaving || isSubmitting}
              >
                {t("save")}
              </GenericButton>
            </div>
          </form>
        </Box>
      </Modal>

      {errorModal}

      <GenericNotification
        message={showSuccessNotification ? successMessage : null}
        variant="success"
        handleCloseSnackbar={() => setShowSuccessNotification(false)}
      />
    </>
  );
}
